import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { Subscription, firstValueFrom, interval } from 'rxjs';
import { Notification } from '../models/notification';
import { AuthService } from './auth.service';

export const NOTIFICATIONS_POLL_INTERVAL_MS = 60_000;

/**
 * CRAT-1302 — owns the unread badge. Polls every 60 s while a user is logged in, plus on window
 * focus / tab visible, so a release note lands within a minute without any push infrastructure.
 * Instantiated from AppComponent so the login/logout subscription exists for the app's lifetime.
 */
@Injectable({ providedIn: 'root' })
export class NotificationsService {
  #http = inject(HttpClient);
  #authService = inject(AuthService);

  readonly unreadCount = signal(0);

  #poll?: Subscription;
  // CRAT-1302 — generation counter bumped by start()/stop(). refreshUnreadCount() captures it before
  // the request and only applies the response if it's still current, so a GET that was still in
  // flight when the user logged out (or a different account logged in on a shared terminal) can't
  // overwrite the count with a value that no longer applies.
  #session = 0;
  #onFocus = () => void this.refreshUnreadCount();
  #onVisibility = () => {
    if (document.visibilityState === 'visible') {
      void this.refreshUnreadCount();
    }
  };

  constructor() {
    this.#authService.user$.subscribe((user) => (user ? this.start() : this.stop()));
  }

  start() {
    if (this.#poll) {
      return;
    }
    this.#session += 1;
    void this.refreshUnreadCount();
    this.#poll = interval(NOTIFICATIONS_POLL_INTERVAL_MS).subscribe(() => void this.refreshUnreadCount());
    window.addEventListener('focus', this.#onFocus);
    document.addEventListener('visibilitychange', this.#onVisibility);
  }

  stop() {
    this.#session += 1;
    this.#poll?.unsubscribe();
    this.#poll = undefined;
    window.removeEventListener('focus', this.#onFocus);
    document.removeEventListener('visibilitychange', this.#onVisibility);
    this.unreadCount.set(0);
  }

  async refreshUnreadCount(): Promise<void> {
    const session = this.#session;
    try {
      const { count } = await firstValueFrom(
        this.#http.get<{ count: number }>('notifications/unread-count', {
          headers: { 'X-Cratebase-Silent-401': '1' },
        })
      );
      if (session === this.#session) {
        this.unreadCount.set(count);
      }
    } catch {
      // Silent by design (spec §8): the dot simply waits for the next tick; 401 is handled by the interceptor.
    }
  }

  async list(skip: number, limit: number): Promise<{ items: Notification[]; total: number }> {
    const params = new HttpParams().set('skip', skip).set('limit', limit);
    return firstValueFrom(this.#http.get<{ items: Notification[]; total: number }>('notifications', { params }));
  }

  async markRead(id: string): Promise<void> {
    await firstValueFrom(this.#http.post<void>(`notifications/${id}/read`, {}));
    this.unreadCount.update((count) => Math.max(0, count - 1));
  }

  async markAllRead(): Promise<void> {
    await firstValueFrom(this.#http.post<{ updated: number }>('notifications/read-all', {}));
    this.unreadCount.set(0);
  }
}
